import { appLogger } from '@bco/app-logger';

const RELOAD_KEY = 'mf:reloads';
const MAX_RELOADS = 1;

const DYNAMIC_IMPORT_ERROR_SUBSTRINGS = [
  'Failed to fetch dynamically imported module',
  'Failed to load module script',
  'error loading dynamically imported module',
  'Importing a module script failed',
];

function isDynamicImportError(err: unknown): boolean {
  if (!(err instanceof Error) || typeof err.message !== 'string') {
    return false;
  }
  const message = err.message;
  return DYNAMIC_IMPORT_ERROR_SUBSTRINGS.some((substr) =>
    message.includes(substr)
  );
}

export async function loadRemote<T>(loader: () => Promise<T>): Promise<T> {
  try {
    const result = await loader();
    try {
      sessionStorage.removeItem(RELOAD_KEY);
    } catch {
      // sessionStorage may be unavailable (Safari ITP, private mode); ignore.
    }
    return result;
  } catch (err) {
    if (!isDynamicImportError(err)) {
      throw err;
    }

    let reloads: number;
    try {
      reloads = Number(sessionStorage.getItem(RELOAD_KEY) ?? '0');
    } catch {
      // Read failed — can't track budget, treat as exhausted.
      throw err;
    }

    if (reloads < MAX_RELOADS) {
      try {
        sessionStorage.setItem(RELOAD_KEY, String(reloads + 1));
      } catch {
        // Write failed — without budget tracking a reload would loop; bail.
        throw err;
      }
      appLogger.warn('dynamic import failed; reloading once', {
        error: err instanceof Error ? err.message : String(err),
      });
      const url = new URL(window.location.href);
      url.searchParams.set('_mfreload', String(Date.now()));
      window.location.replace(url.toString());
      return new Promise<T>(() => {});
    }

    try {
      sessionStorage.removeItem(RELOAD_KEY);
    } catch {
      // ignore
    }
    throw err;
  }
}
